import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import FormControl from 'react-bootstrap/FormControl';
import Overlay from 'react-bootstrap/Overlay';
import Tooltip from 'react-bootstrap/Tooltip';
import Lightbox from 'react-image-lightbox';
import AdminLinkify from 'components/AdminLinkify';
import EmojiPicker from 'components/EmojiPicker';
import ProfileIcon from 'components/ProfileIcon';
import TextButton from 'components/TextButton';
import TheoData from 'components/TheoData';
import Youtubify from 'components/Youtubify';
import { getUserAdminLink, isAdmin, isCommunityManager } from 'utils/roles';
import { userShape, wallPostShape } from 'utils/shapes';
import PostAuthor from '../PostAuthor';
import PostActions from './PostActions';
import './Post.css';

class Post extends Component {
  constructor(props) {
    super(props);
    this.target = React.createRef();
    this.textareaRef = React.createRef();

    this.state = {
      lightboxOpen: false,
      message: this.props.post.message,
      show: false,
      showEmojiPicker: false
    };

    this.handleChangeMessage = this.handleChangeMessage.bind(this);
    this.handleCancelEdit = this.handleCancelEdit.bind(this);
    this.closeNetTheo = this.closeNetTheo.bind(this);
    this.handleEditPost = this.handleEditPost.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.post.message !== this.props.post.message) {
      this.setState({ message: this.props.post.message });
    }
  }

  handleChangeMessage(event) {
    this.setState({ message: event.target.value });
  }

  handleCancelEdit(post) {
    this.props.onCancelEditPost();
    this.setState({
      message: post.message
    });
  }

  handleSaveEdit(post) {
    this.props.onSaveEditPost(this.state.message, post);
    this.setState({
      message: post.message
    });
  }

  handleEditPost(post) {
    this.props.onEditPost(post);

    requestAnimationFrame(() => {
      if (this.textareaRef.current) {
        const textarea = this.textareaRef.current;
        textarea.focus();
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);
      }
    });
  }

  toggleEmojiPicker = () => {
    const isEmoji = this.state.showEmojiPicker;
    this.setState({
      showEmojiPicker: !isEmoji
    });
  };

  addEmoji = emoji => {
    const { message } = this.state;
    const text = `${message}${emoji.native}`;

    this.setState({
      message: text,
      showEmojiPicker: false
    });
  };

  renderEdit(post) {
    const { onSaveEditPost } = this.props;
    const disabled =
      !post.imageUrl && (!this.state.message || !this.state.message.trim());
    const disabledSaveBtnClass = disabled ? 'disabled-save' : '';

    return (
      <div className="feed-edit-message">
        <FormControl
          as="textarea"
          value={this.state.message}
          onChange={this.handleChangeMessage}
          className="message-text-area"
          rows="3"
          autoFocus
          ref={this.textareaRef}
        />
        <div className="feed-edit-btns">
          <EmojiPicker
            onEmojiPickerToggle={this.toggleEmojiPicker}
            showEmojiPicker={this.state.showEmojiPicker}
            addEmoji={this.addEmoji}
          />
          <Button
            className="cancel-edit-btn"
            variant="danger"
            onClick={() => this.handleCancelEdit(post)}
          >
            Cancel
          </Button>
          <Button
            className={`save-edit-btn ${disabledSaveBtnClass}`}
            variant="primary"
            onClick={() => onSaveEditPost(this.state.message, post)}
            disabled={disabled}
          >
            Save Changes
          </Button>
        </div>
      </div>
    );
  }

  renderMessageBody(post, user) {
    const {
      isEditingPostOrComment,
      toggleComments,
      onUnpinWallPost,
      onPinWallPost,
      onLockWallPost,
      onUnlockWallPost,
      onLikePost,
      onUnlikePost,
      onDeletePost,
      showLegacyFeed
    } = this.props;

    const lightboxStyleOverrides = {
      overlay: {
        zIndex: '10000'
      }
    };

    const postClassName = showLegacyFeed
      ? 'legacy-feed-message-container'
      : 'feed-message-container';

    const postBodyClassName = showLegacyFeed
      ? 'legacy-feed-message-body'
      : 'feed-message-body';

    return (
      <div className={postClassName}>
        <div className={postBodyClassName}>
          <Youtubify>
            <AdminLinkify author={post.author}>{post.message}</AdminLinkify>
          </Youtubify>
          {post.imageUrl && (
            <TextButton
              className="expand-image"
              onClick={() => this.setState({ lightboxOpen: true })}
            >
              <img
                src={post.imageUrl}
                alt={post.imageAltText || 'user shared'}
              />
            </TextButton>
          )}
          {this.state.lightboxOpen && (
            <Lightbox
              mainSrc={post.imageUrl}
              onCloseRequest={() => this.setState({ lightboxOpen: false })}
              reactModalStyle={lightboxStyleOverrides}
            />
          )}
        </div>
        <PostActions
          post={post}
          user={user}
          showLegacyFeed={showLegacyFeed}
          isEditingPostOrComment={isEditingPostOrComment}
          toggleComments={toggleComments}
          onUnpinWallPost={onUnpinWallPost}
          onPinWallPost={onPinWallPost}
          onLockWallPost={onLockWallPost}
          onUnlockWallPost={onUnlockWallPost}
          onLikePost={onLikePost}
          onUnlikePost={onUnlikePost}
          onEditPost={this.handleEditPost}
          onDeletePost={onDeletePost}
        />
      </div>
    );
  }

  closeNetTheo() {
    this.setState({
      show: !this.state.show
    });
  }

  avatarAction() {
    const { post, user, showLegacyFeed } = this.props;
    const isCm = isCommunityManager(user);
    const authorLink = isCm ? getUserAdminLink(post.author) : null;
    const authorAlt = isCm ? `View ${post.author.displayName} in admin` : '';

    if (authorLink) {
      const authorAvatarButton = (
        <TextButton ref={this.target} onClick={this.closeNetTheo}>
          <ProfileIcon
            size={30}
            avatarData={post.author.avatarData}
            legacyAvatarUrl={post.author.avatarUrl}
            showLegacyFeed={showLegacyFeed}
            withBorder
            alt={authorAlt}
          />
        </TextButton>
      );
      return (
        <div>
          {authorAvatarButton}
          <Overlay
            rootClose={true}
            rootCloseEvent="Click"
            onHide={this.closeNetTheo}
            target={this.target.current}
            show={this.state.show}
            placement="bottom"
          >
            <Tooltip className="post-avatar-tooltip" id="post-avatar-tooltip">
              <TheoData
                link={authorLink}
                userId={post.author.userId}
                onClick={this.closeNetTheo}
              />
            </Tooltip>
          </Overlay>
        </div>
      );
    } else {
      return (
        <div>
          <ProfileIcon
            size={50}
            avatarData={post.author.avatarData}
            legacyAvatarUrl={post.author.avatarUrl}
            showLegacyFeed={showLegacyFeed}
            withBorder
            alt={authorAlt}
          />
        </div>
      );
    }
  }

  render() {
    const { post, user, editingPost, showLegacyFeed } = this.props;
    const isCm = isCommunityManager(user);
    const authorLink = isCm ? getUserAdminLink(post.author) : null;
    const authorAlt = isCm ? `View ${post.author.displayName} in admin` : '';
    const className = showLegacyFeed
      ? 'legacy-original-feed-message'
      : 'original-feed-message';

    return (
      <div className={className}>
        <div className="feed-message-specifics">
          {this.avatarAction()}
          <PostAuthor
            post={post}
            link={authorLink}
            linkAlt={authorAlt}
            adminView={isAdmin(user)}
            showLegacyFeed={showLegacyFeed}
          />
        </div>
        {editingPost === post.id
          ? this.renderEdit(post)
          : this.renderMessageBody(post, user)}
      </div>
    );
  }
}

Post.propTypes = {
  user: userShape,
  post: wallPostShape,
  isEditingPostOrComment: PropTypes.func,
  onDeletePost: PropTypes.func,
  toggleComments: PropTypes.func,
  onLikePost: PropTypes.func,
  onUnlikePost: PropTypes.func,
  onPinWallPost: PropTypes.func,
  onUnpinWallPost: PropTypes.func,
  onLockWallPosts: PropTypes.func,
  onUnlockWallPost: PropTypes.func,
  showLegacyFeed: PropTypes.bool
};

export default Post;
